import {
  Bot,
  Inbox,
  ListChecks,
  Loader2,
  Megaphone,
  Moon,
  Plus,
  Sparkle,
  Sun,
  UserPlus,
  Users,
} from 'lucide-react';
import { useTheme } from 'next-themes';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from '@/components/ui/command';
import { useSemanticSearch } from '@/features/shared/hooks/useSemanticSearch';
import { usePermissions } from '@/hooks/usePermissions';
import { useNewProspectModal } from '@/stores/useNewProspectModal';

import { NAV_META } from '@/copy/nav';

import { entradasDe, type Seccion } from './navegacion/registro';
import { useRegistroVisible } from './navegacion/useRegistroVisible';

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Destinos visibles aplanados (entrada + ícono propio o el de su espacio). */
export function destinosVisibles(arbol: Seccion[]) {
  return entradasDe(arbol).map(({ entrada, espacio }) => ({
    id: entrada.id,
    to: entrada.to,
    kbd: entrada.kbd,
    /** Nombre del espacio, o null si la entrada está suelta. */
    espacio: espacio ? (NAV_META[espacio.id]?.label ?? espacio.id) : null,
    icon: (entrada.icon ?? espacio?.icon) as LucideIcon,
  }));
}

/**
 * CommandPalette · cmdk over shadcn Dialog.
 *
 * Permite navegar (TODAS las pantallas a las que el user tiene acceso),
 * ejecutar acciones rápidas (gated por permiso real, no solo por la ruta),
 * buscar prospectos/tareas por semántica y togglear tema. Se abre con ⌘K /
 * Ctrl+K (atajo global manejado por useGlobalShortcuts).
 *
 * Permission-gating: las acciones y los destinos de navegación se filtran con
 * `usePermissions().has(...)`. Un empleado ya NO ve "Invitar usuario" ni "Ver
 * agentes IA" porque no tiene `users.manage` / `ai_rules.read.all`.
 */
export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { resolvedTheme, setTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const { has } = usePermissions();
  const arbolVisible = useRegistroVisible();
  const openNewProspect = useNewProspectModal((s) => s.openModal);

  // Búsqueda semántica · solo dispara si query >= 2 chars (debounced 350ms)
  const [query, setQuery] = useState('');
  const { data: semanticHits = [], isFetching: searchingSemantic } =
    useSemanticSearch(query);
  // Limpiar query al cerrar para no dejar resultados viejos en cache visible
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  function go(path: string) {
    onOpenChange(false);
    navigate(path);
  }

  function runAction(fn: () => void) {
    onOpenChange(false);
    fn();
  }

  function toggleTheme() {
    onOpenChange(false);
    setTheme(isDark ? 'light' : 'dark');
  }

  // Navegación visible según permisos (mismo árbol que el menú lateral).
  const navItems = destinosVisibles(arbolVisible);

  // Acciones rápidas · cada una declara el permiso que la habilita. Se filtran
  // antes de renderizar → el user no ve acciones que no puede ejecutar.
  const actions = [
    {
      id: 'new-prospect',
      label: 'Crear nuevo prospecto',
      value: 'accion crear nuevo prospecto prospect ia manual',
      Icon: Plus,
      shortcut: 'P',
      allowed: has('prospects.write.own'),
      run: () => runAction(openNewProspect),
    },
    {
      id: 'new-campaign',
      label: 'Empezar campaña',
      value: 'accion empezar nueva campaña campaign emails secuencia',
      Icon: Megaphone,
      allowed: has('campaigns.write.own'),
      run: () => go('/campaigns'),
    },
    {
      id: 'review-leads',
      label: 'Revisar leads',
      value: 'accion revisar leads respuestas bandeja inbox conversaciones',
      Icon: Inbox,
      allowed: has('leads.read.own'),
      run: () => go('/leads'),
    },
    {
      id: 'my-tasks',
      label: 'Ver mis tareas',
      value: 'accion ver mis tareas pendientes todo agenda',
      Icon: ListChecks,
      allowed: has('tasks.view'),
      run: () => go('/tareas'),
    },
    {
      id: 'invite-user',
      label: 'Invitar usuario',
      value: 'accion invitar usuario admin equipo',
      Icon: UserPlus,
      allowed: has('users.manage'),
      run: () => go('/equipo?tab=cuentas'),
    },
    {
      id: 'ai-agents',
      label: 'Ver agentes IA activos',
      value: 'accion gobernar agentes ia activos',
      Icon: Bot,
      allowed: has('ai_rules.read.all'),
      run: () => go('/ai-hub?tab=agents'),
    },
  ].filter((a) => a.allowed);

  return (
    <CommandDialog
      open={open}
      onOpenChange={onOpenChange}
      /* El default de DialogContent es sm:max-w-lg = 512px, que en un monitor
         ancho recorta el contexto de cada resultado y deja solo el título. */
      className="sm:max-w-2xl lg:max-w-3xl"
      title="Comando rápido"
      description="Navegá y ejecutá acciones sin tocar el mouse"
    >
      <CommandInput
        value={query}
        onValueChange={setQuery}
        placeholder="Buscar pantallas, acciones, prospectos…"
      />
      <CommandList>
        <CommandEmpty>Sin coincidencias.</CommandEmpty>

        {/*
         * Resultados semánticos · solo visible con query >= 2 chars.
         * Usa OpenAI text-embedding-3-small + RPC core.semantic_search.
         * cmdk filtra por defecto comparando contra item value — para que
         * NUESTROS hits siempre se muestren cuando hay match server-side,
         * usamos `value=query` así cmdk los matchea siempre con la input.
         */}
        {query.trim().length >= 2 && (
          <>
            <CommandGroup
              heading={
                searchingSemantic ? (
                  <span className="flex items-center gap-1.5">
                    <Loader2 className="h-3 w-3 animate-spin" /> Buscando…
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5">
                    <Sparkle className="text-primary h-3 w-3" /> Resultados
                  </span>
                )
              }
            >
              {semanticHits.map((hit) => {
                const Icon = hit.entity_type === 'prospect' ? Users : ListChecks;
                const kindLabel =
                  hit.entity_type === 'prospect' ? 'prospecto' : 'tarea';
                return (
                  <CommandItem
                    key={`${hit.entity_type}-${hit.id}`}
                    value={`${query} ${hit.entity_type} ${hit.title}`}
                    onSelect={() => go(hit.url)}
                  >
                    <Icon className="text-muted-foreground mr-2 h-4 w-4 shrink-0" />
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span className="text-foreground truncate text-body">
                        {hit.title}
                      </span>
                      {hit.subtitle && (
                        <span className="text-fg-dim truncate text-micro">
                          {hit.subtitle}
                        </span>
                      )}
                    </div>
                    <span className="section-label ml-2 shrink-0">
                      {kindLabel}
                    </span>
                  </CommandItem>
                );
              })}
              {!searchingSemantic && semanticHits.length === 0 && (
                <CommandItem disabled value={`empty-${query}`}>
                  <span className="text-fg-dim text-caption italic">
                    Sin resultados semánticos para "{query}"
                  </span>
                </CommandItem>
              )}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        <CommandGroup heading="Navegar">
          {navItems.map((entry) => {
            const meta = NAV_META[entry.id];
            const label = meta?.label ?? entry.id;
            const contexto = entry.espacio;
            return (
              <CommandItem
                key={entry.id}
                value={`navegar ${contexto ?? ''} ${label}`}
                onSelect={() => go(entry.to)}
              >
                <entry.icon className="text-muted-foreground mr-2 h-4 w-4 shrink-0" />
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate">{label}</span>
                  {meta?.description && (
                    <span className="text-fg-dim truncate text-micro">
                      {meta.description}
                    </span>
                  )}
                </div>
                {contexto && (
                  <span className="text-fg-muted ml-2 shrink-0 text-micro">{contexto}</span>
                )}
                {entry.kbd && <CommandShortcut>{entry.kbd}</CommandShortcut>}
              </CommandItem>
            );
          })}
        </CommandGroup>

        {actions.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Acciones">
              {actions.map((action) => (
                <CommandItem
                  key={action.id}
                  value={action.value}
                  onSelect={action.run}
                >
                  <action.Icon className="text-muted-foreground mr-2 h-4 w-4" />
                  {action.label}
                  {action.shortcut && (
                    <CommandShortcut>{action.shortcut}</CommandShortcut>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        <CommandSeparator />

        <CommandGroup heading="Sistema">
          <CommandItem value="sistema cambiar tema" onSelect={toggleTheme}>
            {isDark ? (
              <Sun className="text-muted-foreground mr-2 h-4 w-4" />
            ) : (
              <Moon className="text-muted-foreground mr-2 h-4 w-4" />
            )}
            Cambiar a tema {isDark ? 'claro' : 'oscuro'}
            <CommandShortcut>T</CommandShortcut>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}

/**
 * Hook helper · maneja apertura del palette + atajos globales (T tema, 0-9 nav).
 * Devuelve `{open, setOpen}` para que Topbar pueda renderizar el palette + botón.
 */
export function useCommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { resolvedTheme, setTheme } = useTheme();
  const arbolVisible = useRegistroVisible();

  useEffect(() => {
    const destinos = destinosVisibles(arbolVisible);
    function onKey(e: KeyboardEvent) {
      // ⌘K / Ctrl+K
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
        return;
      }

      // No interceptar si el usuario está escribiendo
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      const editable = target?.isContentEditable;
      if (editable || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
        return;
      }

      // T → toggle tema
      if (e.key === 't' || e.key === 'T') {
        e.preventDefault();
        setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');
        return;
      }

      // 0-9 → navegar al destino cuyo shortcut coincide (no por índice, así
      // soporta el 0 = Ajustes y deja huecos sin romper el mapeo).
      if (/^[0-9]$/.test(e.key)) {
        const entry = destinos.find((n) => n.kbd === e.key);
        if (entry) {
          e.preventDefault();
          navigate(entry.to);
        }
      }
    }

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate, resolvedTheme, setTheme, arbolVisible]);

  return { open, setOpen };
}
